import { useMutation, useTranslation } from '@gimlite/common';
import { useStoreUIV2, Wizard, WizardItem } from '@gimlite/ui-v2';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { updateAccountGql } from './gql/updateAccount.gql';
import { InformationStep } from './steps/information.step';

type UpdateAccountMutation = {
  updateAccount: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    username?: string | null;
    avatar?: string | null;
  };
};

type UpdateAccountVariables = {
  input: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    username?: string | null;
    avatar?: string | null;
  };
};

export type UpdateProfileWizardContext = {
  avatar?: string | null;
  lastName?: string | null;
  firstName?: string | null;
  email?: string | null;
  username?: string | null;
};

export const UpdateProfileWizard = observer(() => {
  const { t } = useTranslation();
  const { SSOStore } = useStoreUIV2();

  const account = SSOStore?.account;

  const [callUpdateAccount, queryUpdateAccount] = useMutation<
    UpdateAccountMutation,
    UpdateAccountVariables
  >(updateAccountGql);

  useEffect(() => {
    if (queryUpdateAccount.data?.updateAccount && SSOStore?.account) {
      const updatedData = queryUpdateAccount.data.updateAccount;
      runInAction(() => {
        if (SSOStore.account) {
          if (updatedData.firstName !== undefined) {
            SSOStore.account.firstName = updatedData.firstName;
          }
          if (updatedData.lastName !== undefined) {
            SSOStore.account.lastName = updatedData.lastName;
          }
          if (updatedData.email !== undefined) {
            SSOStore.account.email = updatedData.email;
          }
          if (updatedData.username !== undefined) {
            SSOStore.account.username = updatedData.username;
          }
          if (updatedData.avatar !== undefined) {
            SSOStore.account.avatar = updatedData.avatar;
          }
        }
      });
    }
  }, [queryUpdateAccount.data, SSOStore]);

  return (
    <Wizard<UpdateProfileWizardContext>
      defaultContext={{
        avatar: account?.avatar,
        lastName: account?.lastName,
        firstName: account?.firstName,
        email: account?.email,
        username: account?.username,
      }}
      subtitle={t('watermelon-profile')}
      title={t('watermelon-updatePersonalInformation')}
      finishedDescription={
        queryUpdateAccount.data?.updateAccount?.firstName &&
        queryUpdateAccount.data?.updateAccount?.lastName
          ? `${queryUpdateAccount.data.updateAccount.firstName} ${queryUpdateAccount.data.updateAccount.lastName}`
          : null
      }
      isLoading={queryUpdateAccount.loading}
      isFinished={!!queryUpdateAccount.data}
      isError={!!queryUpdateAccount.error}
    >
      <WizardItem
        enableDraftButton={false}
        enableCreateOtherButton={false}
        textPrimaryButton={t('watermelon-modify')}
        enableFooter={true}
        component={InformationStep}
        eventPrimaryButton={async (context: UpdateProfileWizardContext) => {
          await callUpdateAccount({
            variables: {
              input: {
                lastName: context.lastName,
                firstName: context.firstName,
                email: context.email,
                avatar: context.avatar,
                // username n'est pas modifiable, donc on ne l'envoie pas
              },
            },
          });
        }}
      />
    </Wizard>
  );
});
